import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Floating banner for select-all functionality.
 *
 * Hidden until at least one checkbox is selected. Then appears as a
 * floating bar above the grid with selection count + actions.
 */
const SelectAllBanner = ({
    selectedIds,
    selectAllMeta,
    onSelectAll,
    onDeselectAll,
    loading,
}) => {
    const isAllSelected = selectAllMeta !== null;
    // When select-all is active the grid only holds the current page's IDs, so
    // the full count has to come from the meta. Two shapes reach us: RecordList
    // puts the resolved IDs in `meta.ids`, while `useSelectAll` reports only
    // `{totalCount, limit}`. Reading `meta.ids` alone made the count 0 for the
    // latter, so the banner unmounted itself the instant you clicked
    // "Select all matching" — fall through to the other two.
    const count = isAllSelected
        ? (selectAllMeta.ids?.length
            || selectedIds?.length
            || selectAllMeta.totalCount
            || 0)
        : (selectedIds?.length || 0);
    if (count === 0) return null;

    // The id list is capped, but that only LIMITS anything when the grid's bulk
    // action needs the ids. Export re-sends the filters instead, so on those
    // grids every matching row really is selected and saying "(max)" would be
    // telling the user their action is bounded when it is not.
    const isLimited = selectAllMeta
        && !selectAllMeta.allMatchingCapable
        && selectAllMeta.totalCount > selectAllMeta.limit;
    const isAllMatching = isAllSelected && selectAllMeta.allMatchingCapable;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                px: 2,
                py: 0.75,
                bgcolor: '#fff',
                border: '1px solid #90caf9',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'nowrap',
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                {isLimited
                    ? `${selectAllMeta.limit.toLocaleString()} of ${selectAllMeta.totalCount.toLocaleString()} selected (max)`
                    : isAllMatching
                        ? `All ${selectAllMeta.totalCount.toLocaleString()} matching selected`
                        : `${count.toLocaleString()} row${count !== 1 ? 's' : ''} selected`}
            </Typography>
            {!isAllSelected && onSelectAll && (
                <Button
                    size="small"
                    variant="text"
                    startIcon={loading ? <CircularProgress size={14} /> : <SelectAllIcon fontSize="small" />}
                    onClick={onSelectAll}
                    disabled={loading}
                    sx={{ textTransform: 'none' }}
                >
                    Select all matching
                </Button>
            )}
            {onDeselectAll && (
                <Button
                    size="small"
                    variant="text"
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={onDeselectAll}
                    sx={{ textTransform: 'none' }}
                >
                    Clear
                </Button>
            )}
        </Box>
    );
};

export default SelectAllBanner;
